import { useCurrentUser } from '../../modules/User/useCurrentUser'
import { TZone, useZones } from '../../modulesCommon/serviceHooks/useZones'
import { useZoneParams } from './useZoneParams'
import { generatePath, useLocation } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { COUNTRY_CODE_PERSIST_KEY, ZONE_ID_PERSIST_KEY } from '../../modulesCommon/serviceHooks'
import { resetAllFilters } from '../utils/resetAllFilters'
import { countryCodeVar, labelCodeIdVar, labelCodeVar, labelTitleVar } from '../../modulesCommon/store'

function replaceSpacesToUnderscores(str: string) {
  return str.replace(/\s/g, '_')
}

function replaceUnderscoresToSpaces(str?: string) {
  if (!str) {
    return ''
  }
  return str.replace(/_/g, ' ')
}

function removeDoubleSlashes(str: string) {
  return str.replace(/\/\//g, '/')
}


export function useZoneManager() {
  const location = useLocation()
  const { zoneAlias: urlZoneAlias, countryCode: urlCountryCode } = useZoneParams()
  const { getZoneById, getZoneByAlias, zonesLoading } = useZones()
  const { user } = useCurrentUser()
  const [userZone, setUserZone] = useState<TZone>()
  const [currentZone, setCurrentZone] = useState<TZone>()
  const [currentCountryCode, setCurrentCountryCode] = useState<string>('')

  useEffect(() => {
    if (zonesLoading) return
    if (!user) return
    const userZone = getZoneById(user.zoneId)
    if (userZone) setUserZone(userZone)
  }, [user, getZoneById, zonesLoading])

  useEffect(() => {
    if (zonesLoading) return
    // Exiting if no zoneAlias in url
    if (!urlZoneAlias) return
    const zone = getZoneByAlias(replaceUnderscoresToSpaces(urlZoneAlias))
    if (zone) {
      setCurrentZone(zone)
      setCurrentCountryCode(urlCountryCode)
    }
  }, [urlCountryCode, currentCountryCode, currentZone, getZoneByAlias, urlZoneAlias, zonesLoading])

  /**
   * Returns true if zoneAlias is valid and countryCode is valid for this zone
   */
  const isValidZoneAndCountry = useCallback((zoneAlias: string, countryCode: string) => {
    const zone = getZoneByAlias(replaceUnderscoresToSpaces(zoneAlias))
    return zone && zone.countryCodes.includes(countryCode)
  }, [getZoneByAlias])

  /**
   * Returns default country code for zone
   * if zone is user's zone, returns user's default country code
   * else returns zone's default country code(first in the list)
   */
  const getDefaultCountryCode = useCallback((zone: TZone) => {
    if (zone.id === user?.zoneId) {
      return user?.country_code
    } else {
      return zone.countryCodes[0]
    }
  }, [user?.country_code, user?.zoneId])

  /**
   * Builds url for zone in next format: /:zoneAlias/:countryCode/:suffix
   * Zone is selected by priority:
   * 1. newZone (if provided)
   * 2. currentZone (if exists)
   * 3. userZone (if exists)
   *
   * Country code is selected by priority:
   * 1. newCountryCode (if provided)
   * 2. zone's default country code
   */
  const buildZoneUrl = useCallback((suffix: string, newZone?: TZone, newCountryCode?: string) => {
    let zone: TZone
    let countryCode: string
    if (newZone) {
      // If newZone is provided, use it and its default country code
      zone = newZone
      countryCode = getDefaultCountryCode(zone)
    } else if (currentZone) {
      // If no newZone, use currentZone and currentCountryCode
      zone = currentZone
      countryCode = currentCountryCode
    } else if (userZone) {
      // If no currentZone, use userZone and its default country code
      zone = userZone
      countryCode = getDefaultCountryCode(zone)
    } else {
      return ''
    }
    const path = generatePath('/:zoneAlias/:countryCode/*', {
      zoneAlias: replaceSpacesToUnderscores(zone.alias),
      countryCode: newCountryCode ?? countryCode,
      '*': suffix
    })

    return removeDoubleSlashes(path)
  }, [currentCountryCode, currentZone, getDefaultCountryCode, userZone])

  /**
   * Replaces only the zoneAlias in current url
   * @returns new url
   */
  const replaceZoneInUrl = useCallback((newZone: TZone): string => {
    const currentZonePrefix = buildZoneUrl('') // => /:currentZoneAlias/:currentCountryCode
    const newZonePrefix = buildZoneUrl('', newZone) // => /:newZoneAlias/:zone'sDefaultCountryCode

    return location.pathname.replace(currentZonePrefix, newZonePrefix)
  }, [buildZoneUrl, location.pathname])

  /**
   * Replaces only the countryCode in current url
   * @returns new url
   */
  const replaceCountryCodeInUrl = useCallback((countryCode: string): string => {
    const currentZonePrefix = buildZoneUrl('') // => /:currentZoneAlias/:currentCountryCode
    const newZonePrefix = buildZoneUrl('', currentZone, countryCode)  // => /:currentZoneAlias/:newCountryCode

    return location.pathname.replace(currentZonePrefix, newZonePrefix)
  }, [buildZoneUrl, currentZone, location.pathname])

  /**
   * @deprecated Do not use that function in new code
   * included for backward compatibility
   */
  const setZoneLegacy = useCallback((labelId: string | number, countryCode: string) => {
    const zone = getZoneById(+labelId)
    const strLabelId = labelId.toString()
    if (zone) {
      resetAllFilters()
      labelCodeIdVar(strLabelId)
      localStorage.setItem(ZONE_ID_PERSIST_KEY, strLabelId)

      countryCodeVar(countryCode)
      localStorage.setItem(COUNTRY_CODE_PERSIST_KEY, countryCode)


      labelTitleVar(zone.name)
      labelCodeVar(zone.alias)
    }
  }, [getZoneById])

  return {
    loading: zonesLoading,
    replaceCountryCodeInUrl: replaceCountryCodeInUrl,
    replaceZoneInUrl: replaceZoneInUrl,
    isValidZoneAndCountry: isValidZoneAndCountry,
    buildZoneUrl,
    currentZone,
    currentCountryCode,
    setZoneLegacy
  }
}
